import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Toaster } from '@/components/ui/sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, Cell } from 'recharts';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import html2canvas from 'html2canvas';
import { Download, ArrowLeft, TrendingDown, Loader2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Results = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [downloading, setDownloading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState(location.state?.results);
  const [leadData, setLeadData] = useState(location.state?.leadData || {
    first_name: '',
    last_name: '',
    company_name: '',
    email: '',
    phone_number: ''
  });

  useEffect(() => {
    // Check if we have a lead_id in URL params
    const searchParams = new URLSearchParams(location.search);
    const leadId = searchParams.get('lead_id');
    
    if (leadId && !results) {
      // Fetch the lead data from backend
      fetchLeadData(leadId);
    } else if (!results && !leadId) {
      navigate('/');
    }
  }, [location, results, navigate]);

  const fetchLeadData = async (leadId) => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/leads/${leadId}`);
      const lead = response.data;
      setResults(lead.calculation_data);
      setLeadData({
        first_name: lead.first_name,
        last_name: lead.last_name,
        company_name: lead.company_name,
        email: lead.email,
        phone_number: lead.phone_number
      });
    } catch (error) {
      console.error('Failed to fetch lead data:', error);
      toast.error('Failed to load results. Redirecting...');
      setTimeout(() => navigate('/'), 2000);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <Loader2 className="animate-spin" size={48} style={{ margin: '0 auto 16px', color: '#A10007' }} />
          <p style={{ color: '#6b7280' }}>Loading your results...</p>
        </div>
      </div>
    );
  }

  if (!results) return null;

  const { direct_hire, forge, savings, inputs } = results;

  // Prepare chart data
  const comparisonData = [
    {
      name: 'Direct Hire',
      cost: Math.round(direct_hire.all_employees)
    },
    {
      name: 'Forge Staffing',
      cost: Math.round(forge.all_employees)
    }
  ];

  const breakdownData = [
    {
      name: 'Wages & Benefits',
      cost: Math.round(direct_hire.per_employee.burdened_wage)
    },
    {
      name: 'Recruiting & Training',
      cost: Math.round(direct_hire.per_employee.recruiting_onboarding)
    },
    {
      name: 'Vacancy Cost',
      cost: Math.round(direct_hire.per_employee.vacancy_cost)
    },
    {
      name: 'Ramp-up Loss',
      cost: Math.round(direct_hire.per_employee.rampup_cost)
    }
  ];

  const handleDownloadPDF = async () => {
    setDownloading(true);
    try {
      await generatePDF();
      toast.success('PDF downloaded successfully!');
    } catch (error) {
      console.error('PDF generation error:', error);
      toast.error('Failed to generate PDF. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const generatePDF = async () => {
    const doc = new jsPDF();

    // Add header background - #102947 (16, 41, 71)
    doc.setFillColor(16, 41, 71);
    doc.rect(0, 0, 210, 35, 'F');

    // Add logo at top using backend proxy to avoid CORS
    try {
      // Fetch logo through backend proxy
      const response = await axios.get(`${API}/logo-proxy`);
      const logoBase64 = response.data.image;
      
      // Add logo on dark background
      doc.addImage(logoBase64, 'PNG', 15, 8, 60, 20);
      console.log('Logo added successfully to PDF');
    } catch (error) {
      console.error('Failed to load logo:', error);
      // Add text fallback if logo fails
      doc.setFontSize(16);
      doc.setTextColor(255, 255, 255);
      doc.text('FORGE INDUSTRIAL STAFFING', 15, 20);
    }
    
    // Title - Updated color to #A10007 (161, 0, 7)
    doc.setFontSize(22);
    doc.setTextColor(161, 0, 7);
    doc.text('Staffing Cost Savings Report', 20, 50);
    
    doc.setFontSize(10);
    doc.setTextColor(100);
    doc.text(`Generated on ${new Date().toLocaleDateString()}`, 20, 57);

    // Company Info
    if (leadData.company_name) {
      doc.setFontSize(12);
      doc.setTextColor(0);
      doc.text(`Company: ${leadData.company_name}`, 20, 70);
      doc.text(`Contact: ${leadData.first_name} ${leadData.last_name}`, 20, 77);
    }

    // Savings Summary Box - Branded with Forge colors #A10007 (161, 0, 7)
    doc.setFillColor(161, 0, 7);
    doc.rect(20, 90, 170, 35, 'F');
    doc.setFontSize(16);
    doc.setTextColor(255, 255, 255);
    doc.text('Your Potential Annual Savings', 25, 100);
    doc.setFontSize(24);
    doc.text(`$${savings.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 25, 112);
    doc.setFontSize(14);
    doc.text(`(${savings.percentage.toFixed(1)}% reduction)`, 25, 120);

    // Input Assumptions
    doc.setFontSize(14);
    doc.setTextColor(161, 0, 7);
    doc.text('Input Assumptions', 20, 140);
    
    autoTable(doc, {
      startY: 145,
      head: [['Parameter', 'Value']],
      body: [
        ['Number of Employees', inputs.employees_count],
        ['Hourly Wage', `$${inputs.hourly_wage.toFixed(2)}`],
        ['Hours Per Week', inputs.hours_per_week],
        ['Turnover Rate', `${(inputs.turnover_rate_pct * 100).toFixed(0)}%`],
        ['Forge Bill Rate', `$${inputs.forge_bill_rate.toFixed(2)}`]
      ],
      theme: 'striped',
      headStyles: { 
        fillColor: [161, 0, 7],
        textColor: [255, 255, 255],
        fontSize: 11,
        fontStyle: 'bold'
      },
      bodyStyles: { fontSize: 10 }
    });

    // Cost Comparison
    const finalY = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(14);
    doc.setTextColor(161, 0, 7);
    doc.text('Annual Cost Comparison', 20, finalY);

    autoTable(doc, {
      startY: finalY + 5,
      head: [['Cost Type', 'Annual Cost']],
      body: [
        ['Direct Hire (All Employees)', `$${direct_hire.all_employees.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
        ['Forge Staffing (All Employees)', `$${forge.all_employees.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
        ['Your Savings', `$${savings.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]
      ],
      theme: 'grid',
      headStyles: { fillColor: [161, 0, 7] },
      bodyStyles: { fontSize: 11 }
    });

    // Cost Breakdown
    const finalY2 = doc.lastAutoTable.finalY + 15;
    if (finalY2 < 250) {
      doc.setFontSize(14);
      doc.setTextColor(161, 0, 7);
      doc.text('Direct Hire Cost Breakdown (Per Employee)', 20, finalY2);

      autoTable(doc, {
        startY: finalY2 + 5,
        head: [['Cost Component', 'Annual Cost']],
        body: [
          ['Wages & Benefits', `$${direct_hire.per_employee.burdened_wage.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
          ['Recruiting & Training', `$${direct_hire.per_employee.recruiting_onboarding.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
          ['Vacancy Cost', `$${direct_hire.per_employee.vacancy_cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`],
          ['Ramp-up Loss', `$${direct_hire.per_employee.rampup_cost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`]
        ],
        theme: 'striped',
        headStyles: { fillColor: [16, 41, 71] }
      });
    }

    // Add new page for benchmarks and charts
    doc.addPage();
    
    // Add header to new page
    doc.setFillColor(16, 41, 71);
    doc.rect(0, 0, 210, 35, 'F');
    
    try {
      const response = await axios.get(`${API}/logo-proxy`);
      const logoBase64 = response.data.image;
      doc.addImage(logoBase64, 'PNG', 15, 8, 60, 20);
    } catch (error) {
      console.error('Failed to load logo on page 2:', error);
    }

    // Calculation Benchmarks Used
    doc.setFontSize(14);
    doc.setTextColor(161, 0, 7);
    doc.text('Calculation Benchmarks Used', 20, 50);

    const benchmarks = results.benchmarks_used;
    autoTable(doc, {
      startY: 55,
      head: [['Benchmark', 'Value']],
      body: [
        ['Payroll Tax', `${(benchmarks.payroll_tax_pct * 100).toFixed(1)}%`],
        ['Benefits Load', `${(benchmarks.benefits_pct * 100).toFixed(1)}%`],
        ['Workers Compensation', `${(benchmarks.workers_comp_pct * 100).toFixed(1)}%`],
        ['Recruiting Cost', `$${benchmarks.recruiting_cost.toLocaleString()}`],
        ['Onboarding/Training Cost', `$${benchmarks.onboarding_training_cost.toLocaleString()}`],
        ['Vacancy Days', benchmarks.vacancy_days],
        ['Vacancy Productivity Loss', `${(benchmarks.vacancy_productivity_loss_pct * 100).toFixed(0)}%`],
        ['Ramp-up Days', benchmarks.rampup_days],
        ['Ramp-up Productivity Loss', `${(benchmarks.rampup_productivity_loss_pct * 100).toFixed(0)}%`],
        ['Weeks Per Year', benchmarks.weeks_per_year]
      ],
      theme: 'striped',
      headStyles: { 
        fillColor: [16, 41, 71],
        textColor: [255, 255, 255],
        fontSize: 11,
        fontStyle: 'bold'
      },
      bodyStyles: { fontSize: 10 }
    });

    // Add charts as images
    const chartY = doc.lastAutoTable.finalY + 15;
    if (chartY < 200) {
      doc.setFontSize(14);
      doc.setTextColor(161, 0, 7);
      doc.text('Visual Cost Analysis', 20, chartY);

      try {
        // Capture the comparison chart
        const comparisonChart = document.querySelector('.recharts-wrapper');
        if (comparisonChart) {
          const canvas = await html2canvas(comparisonChart, { 
            scale: 2,
            backgroundColor: '#ffffff'
          });
          const chartImage = canvas.toDataURL('image/png');
          doc.addImage(chartImage, 'PNG', 20, chartY + 5, 170, 60);
        }
      } catch (error) {
        console.error('Failed to add charts to PDF:', error);
        doc.setFontSize(10);
        doc.setTextColor(100);
        doc.text('Charts available in online report', 20, chartY + 10);
      }
    }

    // Add branded footer - #102947 (16, 41, 71)
    const pageHeight = doc.internal.pageSize.height;
    doc.setFillColor(16, 41, 71);
    doc.rect(0, pageHeight - 15, 210, 15, 'F');
    
    doc.setFontSize(9);
    doc.setTextColor(255, 255, 255);
    doc.text('Forge Industrial Staffing - Your Partner in Workforce Solutions', 105, pageHeight - 7, { align: 'center' });
    
    doc.setFontSize(8);
    doc.setTextColor(200, 200, 200);
    doc.text('Estimates are based on industry benchmarks. Actual costs may vary.', 105, pageHeight - 3, { align: 'center' });

    // Save PDF
    doc.save(`Forge-Staffing-Savings-Report-${new Date().getTime()}.pdf`);
  };

  return (
    <div style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #f5f7fa 0%, #e8edf2 100%)', paddingBottom: '60px' }}>
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div style={{ 
        background: 'linear-gradient(135deg, #1a1a1a 0%, #2a2a2a 100%)', 
        padding: '20px 15px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto' }}>
          <img 
            src="https://customer-assets.emergentagent.com/job_25403674-ae6e-49e1-bb0a-d271228220d4/artifacts/1u0fumrd_Logo%20with%20Text%20%28RED%20LOGO%20WHITE%20FONT%29.png" 
            alt="Forge Industrial Staffing"
            style={{ height: '60px', maxWidth: '200px', objectFit: 'contain' }}
          />
        </div>
      </div>

      {/* Results Content */}
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '60px 20px' }}>
        {/* Back Button */}
        <button
          onClick={() => navigate('/')}
          style={{
            background: 'transparent',
            color: '#6b7280',
            border: 'none',
            padding: '10px 20px',
            borderRadius: '8px',
            fontSize: '15px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            marginBottom: '30px',
            transition: 'all 0.3s ease'
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.background = 'rgba(107, 114, 128, 0.1)';
            e.currentTarget.style.color = '#1a1a1a';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.background = 'transparent';
            e.currentTarget.style.color = '#6b7280';
          }}
          data-testid="back-to-calculator-btn"
        >
          <ArrowLeft size={18} />
          Back to Calculator
        </button>

        {/* Savings Hero */}
        <div style={{
          background: 'linear-gradient(135deg, #C41E3A 0%, #8B0000 100%)',
          borderRadius: '20px',
          padding: 'clamp(30px, 5vw, 50px) clamp(20px, 4vw, 40px)',
          color: 'white',
          textAlign: 'center',
          marginBottom: '40px',
          boxShadow: '0 10px 40px rgba(196, 30, 58, 0.3)'
        }} data-testid="savings-summary">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '12px', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TrendingDown size={32} />
            <h2 style={{ fontSize: 'clamp(18px, 4vw, 24px)', fontWeight: '600' }}>Your Potential Annual Savings</h2>
          </div>
          <div style={{ fontSize: 'clamp(32px, 8vw, 56px)', fontWeight: '700', marginBottom: '12px', wordBreak: 'break-word' }}>
            ${savings.amount.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
          <div style={{ fontSize: 'clamp(16px, 3vw, 22px)', opacity: 0.95 }}>
            That's a {savings.percentage.toFixed(1)}% cost reduction!
          </div>
        </div>

        {/* Charts */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px', marginBottom: '40px' }}>
          {/* Comparison Chart */}
          <div className="chart-container">
            <h3 style={{ fontSize: '20px', marginBottom: '20px', color: '#1a1a1a', fontWeight: '600' }}>Cost Comparison</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={comparisonData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis dataKey="name" />
                <YAxis tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Bar dataKey="cost" radius={[8, 8, 0, 0]}>
                  <Cell fill="#6b7280" />
                  <Cell fill="#C41E3A" />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Breakdown Chart */}
          <div className="chart-container">
            <h3 style={{ fontSize: '20px', marginBottom: '20px', color: '#1a1a1a', fontWeight: '600' }}>Direct Hire Cost Breakdown (Per Employee)</h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={breakdownData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                <XAxis type="number" tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`} />
                <YAxis type="category" dataKey="name" width={150} />
                <Tooltip formatter={(value) => `$${value.toLocaleString()}`} />
                <Bar dataKey="cost" fill="#1a1a1a" radius={[0, 8, 8, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Details Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '20px', marginBottom: '40px' }}>
          <div className="card">
            <h3 style={{ fontSize: '18px', marginBottom: '12px', color: '#C41E3A' }}>Direct Hire Total</h3>
            <p style={{ fontSize: '32px', fontWeight: '700', color: '#1a1a1a' }}>
              ${direct_hire.all_employees.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>Per year for {inputs.employees_count} employees</p>
          </div>

          <div className="card">
            <h3 style={{ fontSize: '18px', marginBottom: '12px', color: '#C41E3A' }}>Forge Staffing Total</h3>
            <p style={{ fontSize: '32px', fontWeight: '700', color: '#1a1a1a' }}>
              ${forge.all_employees.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
            <p style={{ fontSize: '14px', color: '#6b7280', marginTop: '8px' }}>Per year for {inputs.employees_count} employees</p>
          </div>
        </div>

        {/* CTA Button */}
        <div style={{ textAlign: 'center' }}>
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="btn-forge"
            style={{
              fontSize: '18px',
              padding: '18px 48px',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '12px',
              opacity: downloading ? 0.7 : 1
            }}
            data-testid="download-pdf-btn"
          >
            <Download size={20} />
            {downloading ? 'Generating PDF...' : 'Download PDF Report'}
          </button>
          <p style={{ marginTop: '16px', color: '#6b7280', fontSize: '14px' }}>
            Download a detailed PDF report of your cost savings analysis
          </p>
        </div>
      </div>
    </div>
  );
};

export default Results;
